import Cookies from "js-cookie"
import { html, render } from "lit-html"
import { repeat } from "lit-html/directives/repeat"
import { virtual, useEffect, useState } from "haunted"
import { debounce, formToObj } from "../utils"
import { ALT_SORT, DEBOUNCE_TIME, FOR_USER_FILTER } from "../constants"
import { addToastMessage } from "../generic/messages"
import AgencyRow from "./agency"
import AssignmentRow from "./assignment"
import DefaultRow from "./default"
import DocumentRow from "./document"
import MeetingRow from "./meeting"
import NoResults from "./no-results"
import ReportingRow from "./reporting"
import Toggle from "./toggle"
import HeadlineToggle from "./headline-toggle"
import UserRow from "./user"
import WorkshopRow from "./workshop"

const rowComponent = (template, props) => {
  switch (template) {
    case "agency":
    case "agencies":
      return AgencyRow(props)
    case "assignment":
    case "assignments":
      return AssignmentRow(props)
    case "document":
    case "documents":
      return DocumentRow(props)
    case "meeting":
    case "meetings":
      return props.assignment
        ? AssignmentRow({ ...props, ...props.assignment })
        : MeetingRow(props)
    case "workshop":
    case "workshops":
      return WorkshopRow(props)
    case "user":
    case "users":
      return UserRow(props)
    case "reporting":
      return ReportingRow(props)
    default:
      return DefaultRow(props)
  }
}

const setupMobileToggle = (toggleButton) => {
  const toggleBody = document.getElementById(
    toggleButton.getAttribute("aria-controls")
  )

  const updateVisibility = () => {
    const isMobile = window.getComputedStyle(toggleButton).display !== "none"
    const hasMobileHiddenClass = toggleBody.classList.contains("hidden")
    toggleButton.setAttribute(
      "aria-expanded",
      (!isMobile || (isMobile && !hasMobileHiddenClass)).toString()
    )
  }

  const onResize = debounce(updateVisibility, DEBOUNCE_TIME)
  window.addEventListener("resize", onResize)

  toggleButton.addEventListener("click", () => {
    toggleBody.classList.toggle("hidden")
    updateVisibility()
  })

  updateVisibility()
}

const clearFilters = (el) => {
  el.querySelector(`form[name="filter"]`).reset()
  el.querySelectorAll(
    `input[name="search"], input[type="hidden"], select`
  ).forEach((el) => (el.value = ``))
  el.querySelectorAll("input:checked").forEach((checked) => {
    checked.checked = false
  })
  el.querySelectorAll(`.tag-filter-input.tags-checked`).forEach((el) => {
    el.classList.remove("tags-checked")
  })
  el.querySelectorAll(`input[data-has-value]`).forEach((el) => {
    el.dispatchEvent(new Event("change"))
  })
  el.querySelectorAll(`button[data-has-value]`).forEach(($button) => {
    $button.dispatchEvent(new CustomEvent("clear"))
  })
  el.querySelectorAll(`.filter-autocomplete-field ul a`).forEach(($button) => {
    if ($button.textContent.trim() === "Clear") $button.click()
  })
}

const getFormDataObj = (el) => formToObj(el.querySelector("form"))

const getQueryStr = (formData) => {
  // Remove blank values from form object
  Object.entries(formData).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      formData[key] = value.filter((v) => !(typeof v === "string" && !v.trim()))
    }
    if (value === "" || (Array.isArray(value) && value.length === 0))
      delete formData[key]
  })
  return `${new URLSearchParams(formData)}`
}

const sendGaFilterEvent = (queryStr) => {
  if (!window.gtag) {
    return
  }
  window.gtag("event", "filter", {
    event_category: window.location.pathname,
    event_label: queryStr,
    anonymize_ip: true,
  })
}

const sendPosthogFilterEvent = (queryStr) => {
  if (!window.posthog) {
    return
  }
  window.posthog.capture("filter", {
    event_category: window.location.pathname,
    filter_query: queryStr,
  })
}

const updateWindowQueryStr = (queryStr) => {
  window.history.replaceState(
    {},
    window.document.title,
    `${window.location.protocol}//${window.location.host}${window.location.pathname}?${queryStr}`
  )
  sendGaFilterEvent(queryStr)
  sendPosthogFilterEvent(queryStr)
}

const updateResultsInfo = ({ count, previous }) => {
  const countEl = document.getElementById("filter-results-count")
  if (countEl && count !== null && count !== undefined) {
    countEl.innerText = `${count.toLocaleString()} result${
      count !== 1 ? "s" : ""
    }`
  }
  const statusEl = document.getElementById("filter-status")
  // Only update status el if previous is undefined, meaning there's a new filter and
  // not that the user has clicked "Load more"
  if (statusEl && count !== null && count !== undefined && previous === null) {
    statusEl.innerText = `There are ${count} result${
      count !== 1 ? "s" : ""
    } available`
  }
}

const hasActiveFilters = (formData) =>
  // eslint-disable-next-line no-unused-vars
  Array.from(Object.entries(formData)).some(([key, value]) => value !== "")

const loadResults = ({ endpoint, queryStr, page, csrftoken }) =>
  fetch(`${endpoint}?${queryStr}&page=${page}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "X-CSRFToken": csrftoken,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .then((res) => res.json())
    .catch((err) => console.error(err))

const CreatePathButtons = (paths) => {
  if (!paths || paths.length === 0) return html``

  return html`
    <div class="flex">
      ${repeat(
        paths,
        (path) => path.path,
        (path) => html`
          <a
            class="ml-4 button ${path.button} w-9 h-9 md:w-10 md:h-10 lg:w-auto lg:h-auto text-sm md:text-base"
            href="${path.path}"
            aria-label=" ${path.title}"
          >
            <i class="${path.icon}" alt="Visit Example Website"></i>
            <span class="hidden ml-4 lg:inline"> ${path.title} </span>
          </a>
        `
      )}
    </div>
  `
}

export const FilterSet = virtual(
  ({
    el,
    filterSearch,
    csrftoken,
    title,
    createPaths,
    rowTemplate,
    endpoint,
    noResultsMessage,
  }) => {
    const [results, setResults] = useState([])
    const [page, setPage] = useState(1)
    const [didMount, setDidMount] = useState(false)
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [queryStr, setQueryStr] = useState(getQueryStr(getFormDataObj(el)))
    const [altSortConfig, setAltSortConfig] = useState(null)
    const [forUserConfig, setForUserConfig] = useState(null)
    const [showFilter, setShowFilter] = useState(false)

    // Set didMount to control which effects run on initial render
    useEffect(() => setDidMount(true), [])

    const clearFilterButtons = document.querySelectorAll(
      "button[data-action='clear']"
    )

    const onClear = () => {
      clearFilters(el)
      setQueryStr(getQueryStr(getFormDataObj(el)))
    }

    // Because of how it functions, we're handling the alt sort and for user filter input separately
    const formObj = getFormDataObj(el)
    const setAltSort = (altSort) => {
      const altInput = el.querySelector(`[name="${altSortConfig.field}"]`)
      altInput.value = altSort ? "true" : ""
      setQueryStr(getQueryStr(getFormDataObj(el)))
    }
    const setForUser = (forUser) => {
      const forUserInput = el.querySelector(`[name="${forUserConfig.field}"]`)
      forUserInput.value = forUser ? "true" : ""
      setQueryStr(getQueryStr(getFormDataObj(el)))
    }

    // Setup initial event listeners on page load
    useEffect(() => {
      el.querySelectorAll(`[data-action="filter-fields-body-toggle"]`).forEach(
        setupMobileToggle
      )
      el.querySelector(`form[name="filter"]`)
        .querySelectorAll("input, select")
        .forEach((input) => {
          input.addEventListener(
            input.type === "text" ? "input" : "change",
            debounce(
              () => setQueryStr(getQueryStr(getFormDataObj(el))),
              DEBOUNCE_TIME
            )
          )
        })

      clearFilterButtons.forEach((btn) => {
        btn.classList.toggle("hidden", !hasActiveFilters(getFormDataObj(el)))
        btn.addEventListener("click", onClear)
      })

      const altSortInput = el.querySelector(`[data-type="${ALT_SORT}"]`)
      if (altSortInput) {
        setAltSortConfig({
          field: altSortInput.name,
          defaultLabel: altSortInput.dataset.defaultLabel,
          altLabel: altSortInput.dataset.altLabel,
        })
      }

      const $forUserInput = el.querySelector(`[data-type="${FOR_USER_FILTER}"]`)
      if ($forUserInput) {
        setForUserConfig({
          field: $forUserInput.name,
          defaultLabel: $forUserInput.dataset.defaultLabel,
          altLabel: $forUserInput.dataset.altLabel,
        })
      }
    }, [el])

    // Update displayed results based on current filters, endpoint and page
    const updateResults = () => {
      // A -1 page is just to trigger an update, reset this first without requesting
      if (page < 1) {
        setPage(1)
        return
      }
      setLoading(true)
      loadResults({
        endpoint,
        queryStr,
        page,
        csrftoken,
      })
        .then((res) => {
          setLoading(false)
          setHasMore(!!res.next)
          setResults(page === 1 ? res.results : [...results, ...res.results])
          updateResultsInfo(res)
        })
        .catch((e) => {
          console.error(e)
          addToastMessage("There was an error finding your results", "error")
        })
    }

    // Whenever the page changes, update the results
    useEffect(updateResults, [page])

    // Whenever the filters or endpoint changes, reset the page and window query string
    useEffect(() => {
      // Don't run on initial render because initial updateResults already run
      if (!didMount) return
      // Set page as -1 to make sure update is triggered
      setPage(-1)
      updateWindowQueryStr(queryStr)
      // Hide filter buttons if no active filters
      clearFilterButtons.forEach((btn) => {
        btn.classList.toggle("hidden", !hasActiveFilters(getFormDataObj(el)))
      })
    }, [queryStr])

    const noResults = loading
      ? ``
      : NoResults({
          message: noResultsMessage,
          onClear,
        })

    const FilterHeader = (cssStyle) => html`
      <section data-component="filter-header" class="${cssStyle}">
        <div class="py-7 flex justify-between items-center">
          ${forUserConfig
            ? HeadlineToggle({
                section: "for-user-toggle",
                defaultLabel: forUserConfig.defaultLabel,
                altLabel: forUserConfig.altLabel,
                altSort: !!formObj[forUserConfig.field],
                onToggle: setForUser,
              })
            : html`<h1
                class="text-xl md:text-xxl lg:text-3xl text-black font-serif"
              >
                ${title}
              </h1>`}
          ${CreatePathButtons(createPaths)}
        </div>
        <div
          class="flex flex-row-reverse lg:flex-row justify-between items-center text-sm md:text-base"
        >
          <button
            class="flex-grow-0 mb-6 lg:hidden button rounded-md border-none ${showFilter
              ? `bg-blue-200 text-blue`
              : `bg-gray-bg text-gray-interactive`} font-bold"
            type="button"
            @click=${() => setShowFilter(!showFilter)}
          >
            <span class="mr-3">Filters</span>
            <i class="fas fa-filter"></i>
          </button>
          ${altSortConfig
            ? Toggle({
                defaultLabel: altSortConfig.defaultLabel,
                altLabel: altSortConfig.altLabel,
                altSort: !!formObj[altSortConfig.field],
                onToggle: setAltSort,
              })
            : ``}
        </div>
      </section>
    `

    const FilterResult = () => html`
      <section
        class="flex-grow w-full text-sm md:text-base"
        id="filter-result-column"
      >
        <div class="relative loader-container ${loading ? "loading" : ""}">
          <div data-section="filter-results" class="bg-white">
            ${repeat(
              results,
              (row) => row.id,
              (row) =>
                rowComponent(rowTemplate, {
                  ...row,
                  onUpdate: () => setPage(-1),
                })
            )}
            ${results.length === 0 ? noResults : ``}
          </div>
          ${hasMore
            ? html`
                <button
                  class="button secondary mt-2 mb-5 py-5 text-center rounded-lg ${loading
                    ? "loading"
                    : ""}"
                  type="button"
                  @click=${() => setPage(page + 1)}
                >
                  Load more
                </button>
              `
            : ``}
        </div>
      </section>
    `

    return html`
      ${FilterHeader("lg:hidden")}
      <div class="lg:mt-5 flex flex-col md:flex-row">
        <div class="${showFilter ? "block" : "hidden"} lg:block md:mr-6">
          ${filterSearch}
        </div>
        <div class="w-full">
          ${FilterHeader("hidden lg:block")} ${FilterResult()}
        </div>
      </div>
    `
  }
)

export const renderFilterSet = (el) => {
  const {
    title,
    create_paths,
    row_template,
    no_results_message,
    endpoint,
  } = JSON.parse(
    document.querySelector(`[type="application/json"][data-props]`).innerText
  )

  const csrftoken = Cookies.get("csrftoken")
  const filterSearch = el.querySelector(`section[role="search"]`)
  render(
    FilterSet({
      el,
      filterSearch,
      csrftoken,
      title,
      createPaths: create_paths,
      rowTemplate: row_template,
      endpoint,
      noResultsMessage: no_results_message,
    }),
    document.getElementById("filter-mount")
  )
}
